import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, CircularProgress, Alert, Divider,
  FormControl, FormControlLabel, FormHelperText, FormLabel,
  InputLabel, MenuItem, Radio, RadioGroup, Select,
  Step, StepLabel, Stepper, TextField, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { GRADES, ROLES, TSHIRT_SIZES, DIETARY_OPTIONS } from '../shared/participantOptions';
import CheckItem from '../shared/CheckItem';
import { eventYearFrom } from '../shared/eventDate';
import usePageTitle from '../shared/hooks/usePageTitle';

// Each description is JSX, not a plain string, so it can carry <br/> line
// breaks and <strong>/<em> emphasis — the render site below just does
// {l.description}, which works the same whether this holds a string or a
// JSX node.
// The skill examples are coloured so they stand out from the level's
// description text (which renders in the muted text.secondary colour) —
// a lighter blue in dark mode so it stays readable on the dark background.
const SkillExamples = ({ children }) => (
  <Box component="span" sx={{ display: 'block', mt: 0.75, mb: 1, color: (theme) => (theme.palette.mode === 'dark' ? '#85B7EB' : '#185FA5') }}>
    <strong>Skill examples:</strong> {children}
  </Box>
);

const EXPERIENCE_LEVELS = [
  {
    value: 'Novice', description: (
      <>Dancers who are NEW to formal dance training and performance. They have limited experience learning, rehearsing, and performing choreography and have not participated in competitive dance anywhere.
        <br /><br />
        <strong><em>Dancers may perform at this level for one year only</em></strong>.</>
    ),
  },
  {
    value: 'Intermediate 1', description: (
      <>Dancers with developing experience through <strong>school curricular and extra-curricular programming</strong>. They show foundational technique, performance skills, and growing consistency. This level also includes novice dancers who have participated in Dancefest before.
        <SkillExamples>single pirouette, basic leaps, kicks, simple floorwork, basic grooves and isolations.</SkillExamples></>
    ),
  },
  {
    value: 'Intermediate 2', description: (
      <>Dancers with more established training and performance experience. This includes dancers in <strong>recreational or non-competitive studio programs</strong>, dancers with <strong>less than 2 years of pre-competitive studio experience</strong>, and dancers whose experience through <strong>school curricular, extra-curricular, or community programming</strong> have developed a similar level of technical and performance skill.
        <SkillExamples>consistent single turns, developing double turns, stronger leaps, more complex floorwork, higher kicks or extensions, and layered movement and rhythm.</SkillExamples></>
    ),
  },
  {
    value: 'Advanced 1', description: (
      <>Dancers with substantial training and performance experience. This includes dancers in <strong>years 1–3 of studio competitive dance</strong>, as well as dancers whose experience through <strong>school curricular, extra-curricular, or community programming</strong> have developed a similar level of technical and performance skill.
        <SkillExamples>consistent double or multiple turns, more complex turning combinations, switch leaps, advanced jumps, controlled extensions, inversions, complex floorwork, and strong dynamic changes.</SkillExamples></>
    ),
  },
  {
    value: 'Advanced 2', description: (
      <>Dancers with extensive and sustained advanced training and performance experience. This includes dancers in their <strong>4th year of studio competitive dance or beyond</strong>, as well as dancers whose experience through <strong>school curricular, extra-curricular, or community programming</strong> have developed a similarly advanced level of technical and performance skill.
        <SkillExamples>advanced turning combinations, fouettés or à la seconde turns, complex leap and jump combinations, sustained extensions and balances, advanced floorwork, inversions, and intricate rhythmic or musical layering.</SkillExamples></>
    ),
  },
];
const YES_NO = ['Yes', 'No'];

const WAIVER_ITEMS = [
  { key: 'disqualified', label: 'I understand that my dance entries will be disqualified should there be any neglect.' },
  { key: 'expectations', label: 'I understand and will follow the expectations exactly.' },
];

const EMPTY_FORM = {
  schoolId: '', firstName: '', lastName: '', email: '', grade: '',
  role: '', experienceLevel: '', tshirtSize: '',
  graduating: '', firstTimeDancefest: '', firstTimeChoreographer: '',
  dietary: [], dietaryOther: '',
};
const EMPTY_WAIVER = { disqualified: false, expectations: false };

export default function DancerRegistration() {
  usePageTitle('OSSDF Dancer Registration');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const schoolFromUrl = searchParams.get('school') || '';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM, schoolId: schoolFromUrl });
  const [errors, setErrors] = useState({});
  const [waiver, setWaiver] = useState(EMPTY_WAIVER);
  const [feesAgree, setFeesAgree] = useState(false);
  const [waiverError, setWaiverError] = useState(false);
  const [parentMode, setParentMode] = useState(''); // '' | 'now' | 'separate'
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ name: '', email: '' });
  const [sendErrors, setSendErrors] = useState({});
  const [schoolName, setSchoolName] = useState('');
  const [schoolLoadError, setSchoolLoadError] = useState(null);
  const [participantsLocked, setParticipantsLocked] = useState(false);
  const [notRegistered, setNotRegistered] = useState(false); // school hasn't registered for the event
  const [participationFee, setParticipationFee] = useState(null);
  const [eventYear, setEventYear] = useState('');

  // Same reasoning as the prices fetch below — pulled from the Events tab so
  // the year mentioned throughout this form (header, Integrity Agreement)
  // doesn't go stale every festival. Best-effort: falls back to no year
  // shown rather than blocking the form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/event`)
      .then(res => (res.ok ? res.json() : null))
      .then(event => setEventYear(eventYearFrom(event?.startDate)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!schoolFromUrl) return;
    fetch(`${API_BASE}/api/schools/${encodeURIComponent(schoolFromUrl)}`)
      .then(res => {
        if (!res.ok) throw new Error('Could not find this school.');
        return res.json();
      })
      .then(data => {
        setSchoolName(data.schoolName);
        setParticipantsLocked(!!data.participantsLocked);
        setNotRegistered(!!data.notRegistered);
      })
      .catch(err => setSchoolLoadError(err.message));
  }, [schoolFromUrl]);

  // Pulled from INFORMATION_SHEET's Prices tab ("Participation Fee" row) so
  // the Integrity Agreement's fee mention can't drift from what's actually
  // charged. Best-effort: if it fails to load, item 5 just omits the dollar
  // amount rather than blocking the whole form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/prices`)
      .then(res => (res.ok ? res.json() : null))
      .then(prices => setParticipationFee(prices?.['Participation Fee'] ?? null))
      .catch(() => {});
  }, []);

  const isChoreographer = form.role === 'Choreographer' || form.role === 'Dancer and Choreographer';

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({
      ...prev, [field]: value,
      ...(field === 'role' && value === 'Dancer' ? { firstTimeChoreographer: '' } : {}),
      ...(field === 'grade' ? { graduating: value === '12' ? '' : 'No' } : {}),
    }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleDietary = (option) => {
    setForm(prev => {
      const current = Array.isArray(prev.dietary) ? prev.dietary : [];
      if (option === 'None') return { ...prev, dietary: current.includes('None') ? [] : ['None'], dietaryOther: '' };
      const without = current.filter(d => d !== 'None' && d !== option);
      return { ...prev, dietary: current.includes(option) ? without : [...without, option] };
    });
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.schoolId.trim()) e.schoolId = 'Required';
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.grade) e.grade = 'Required';
    if (!form.role) e.role = 'Required';
    if (!form.experienceLevel) e.experienceLevel = 'Required';
    if (!form.tshirtSize) e.tshirtSize = 'Required';
    if (!form.graduating) e.graduating = 'Required';
    if (!form.firstTimeDancefest) e.firstTimeDancefest = 'Required';
    if (isChoreographer && !form.firstTimeChoreographer) e.firstTimeChoreographer = 'Required';
    return e;
  };

  const validateSendForm = () => {
    const e = {};
    if (!sendForm.name.trim()) e.name = 'Required';
    if (!sendForm.email.trim()) e.email = 'Required';
    return e;
  };

  const handleConfirmSend = () => {
    const e = validateSendForm();
    if (Object.keys(e).length > 0) { setSendErrors(e); return; }
    setForm(prev => ({
      ...prev,
      parentGuardianName: sendForm.name,
      parentGuardianEmail: sendForm.email,
    }));
    setSendDialogOpen(false);
  };
  const handleNext = () => {
    if (step === 0) {
      const e = validateStep1();
      if (Object.keys(e).length > 0) { setErrors(e); return; }
    } else if (step === 1) {
      const allChecked = WAIVER_ITEMS.every(item => waiver[item.key]) && feesAgree;
      if (!allChecked) { setWaiverError(true); return; }
    }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    if (!parentMode) return;

    setSubmitting(true);
    setSubmitError(null);

    const dietaryArray = Array.isArray(form.dietary) ? form.dietary : [];
    const dietaryString = dietaryArray.map(d => d === 'Other' && form.dietaryOther ? `Other: ${form.dietaryOther}` : d).join(', ');
    try {
      const res = await fetch(`${API_BASE}/api/register/dancer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, dietary: dietaryString, parentMode }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${res.status}`);
      }
      const data = await res.json(); // <-- now declared on the success path too
      if (parentMode === 'now') {
        navigate(`/registration/parent-consent?id=${encodeURIComponent(data.participantId)}`);
      } else {
        setSubmitted(true); // only show the "link sent" success screen for "separate"
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5" fontWeight={700}>Registration submitted!</Typography>
        <Typography color="text.secondary">
          Thank you, {form.firstName}. Your registration has been received.
        </Typography>

        {parentMode === 'now' && (
          <Typography color="text.secondary">Parent consent has been recorded.</Typography>
        )}
      </Box>
    );
  }

  // ── Registration closed ──────────────────────────────────────────────────────
  if (schoolFromUrl && participantsLocked) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700}>{notRegistered ? 'Registration isn\'t open yet' : 'Registration is closed'}</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
          {notRegistered
            ? `${schoolName || 'This school'} hasn't registered for this event yet, so dancers can't sign up. Check with your teacher.`
            : `${schoolName || 'This school'} has closed new dancer registrations. If you believe this is a mistake, contact your teacher.`}
        </Typography>
      </Box>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, gap: 1 }}>
          <DirectionsRunIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h4" fontWeight={700}>Dancer Registration</Typography>
          <Typography color="text.secondary">OSS Dancefest{eventYear ? ` ${eventYear}` : ''}</Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          <Step><StepLabel>Registration</StepLabel></Step>
          <Step><StepLabel>Integrity Agreement</StepLabel></Step>
          <Step><StepLabel>Parent Consent</StepLabel></Step>
        </Stepper>

        {/* ── Step 0: Registration form ────────────────────────────────────── */}
        {step === 0 && (
          <>
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>School</Typography>
                <TextField
                  label="School" required fullWidth size="small"
                  value={schoolFromUrl ? schoolName : form.schoolId}
                  onChange={schoolFromUrl ? undefined : set('schoolId')}
                  error={!!errors.schoolId || !!schoolLoadError}
                  helperText={errors.schoolId || schoolLoadError || (schoolFromUrl ? 'Pre-filled from your invite link' : 'Enter the School ID provided by your teacher')}
                  InputProps={{ readOnly: !!schoolFromUrl }}
                  sx={schoolFromUrl ? { '& .MuiInputBase-root': { bgcolor: 'action.hover' } } : {}}
                />
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Personal Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField label="First name" required size="small" value={form.firstName} onChange={set('firstName')} error={!!errors.firstName} helperText={errors.firstName} />
                  <TextField label="Last name" required size="small" value={form.lastName} onChange={set('lastName')} error={!!errors.lastName} helperText={errors.lastName} />
                </Box>
                <TextField label="Email address" required size="small" type="email" fullWidth value={form.email} onChange={set('email')} error={!!errors.email} helperText={errors.email} />
                <FormControl size="small" required error={!!errors.grade} fullWidth>
                  <InputLabel>Grade</InputLabel>
                  <Select value={form.grade} label="Grade" onChange={set('grade')}>
                    {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                  {errors.grade && <FormHelperText>{errors.grade}</FormHelperText>}
                </FormControl>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Participation</Typography>
                <FormControl size="small" required error={!!errors.role} fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select value={form.role} label="Role" onChange={set('role')}>
                    {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                  {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                </FormControl>
                <FormControl required error={!!errors.experienceLevel} fullWidth>
                  <FormLabel sx={{ fontSize: 14, mb: 1, color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}>
                    Experience level
                  </FormLabel>
                  <RadioGroup value={form.experienceLevel} onChange={set('experienceLevel')}>
                    {EXPERIENCE_LEVELS.map(l => (
                      <FormControlLabel
                        key={l.value} value={l.value}
                        control={<Radio size="small" sx={{ mt: '-2px', alignSelf: 'flex-start' }} />}
                        label={
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>{l.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{l.description}</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 0.5 }}
                      />
                    ))}
                  </RadioGroup>
                  {errors.experienceLevel && <FormHelperText>{errors.experienceLevel}</FormHelperText>}
                </FormControl>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 4 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Additional Information</Typography>
                {form.grade === '12' && (
                  <FormControl size="small" required error={!!errors.graduating} fullWidth>
                    <InputLabel>Are you graduating this year?</InputLabel>
                    <Select value={form.graduating} label="Are you graduating this year?" onChange={set('graduating')}>
                      {YES_NO.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                    {errors.graduating && <FormHelperText>{errors.graduating}</FormHelperText>}
                  </FormControl>
                )}
                <FormControl size="small" required error={!!errors.firstTimeDancefest} fullWidth>
                  <InputLabel>Is this your first time at Dancefest?</InputLabel>
                  <Select value={form.firstTimeDancefest} label="Is this your first time at Dancefest?" onChange={set('firstTimeDancefest')}>
                    {YES_NO.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                  {errors.firstTimeDancefest && <FormHelperText>{errors.firstTimeDancefest}</FormHelperText>}
                </FormControl>
                {isChoreographer && (
                  <FormControl size="small" required error={!!errors.firstTimeChoreographer} fullWidth>
                    <InputLabel>Is this your first time choreographing for ANY competition?</InputLabel>
                    <Select value={form.firstTimeChoreographer} label="Is this your first time choreographing?" onChange={set('firstTimeChoreographer')}>
                      {YES_NO.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                    {errors.firstTimeChoreographer && <FormHelperText>{errors.firstTimeChoreographer}</FormHelperText>}
                  </FormControl>
                )}
                <FormControl size="small" required error={!!errors.tshirtSize} fullWidth>
                  <InputLabel>T-shirt size</InputLabel>
                  <Select value={form.tshirtSize} label="T-shirt size" onChange={set('tshirtSize')}>
                    {TSHIRT_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                  {errors.tshirtSize && <FormHelperText>{errors.tshirtSize}</FormHelperText>}
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel sx={{ fontSize: 14, mb: 0.5, color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}>
                    Dietary restrictions / allergies
                  </FormLabel>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', mt: 0.5 }}>
                    {DIETARY_OPTIONS.map(option => (
                      <FormControlLabel key={option} label={option}
                        control={<Checkbox size="small" checked={Array.isArray(form.dietary) && form.dietary.includes(option)} onChange={() => toggleDietary(option)} />}
                      />
                    ))}
                  </Box>
                  {Array.isArray(form.dietary) && form.dietary.includes('Other') && (
                    <TextField size="small" fullWidth placeholder="Please specify…"
                      value={form.dietaryOther} onChange={e => setForm(prev => ({ ...prev, dietaryOther: e.target.value }))} sx={{ mt: 1 }} />
                  )}
                </FormControl>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate('/registration')}>Cancel</Button>
              <Button variant="contained" onClick={handleNext}>Next: Integrity Agreement</Button>
            </Box>
          </>
        )}

        {/* ── Step 1: Integrity Agreement ──────────────────────────────────── */}
        {step === 1 && (
          <>
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Integrity Agreement</Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Between Choreographers and Dancers and Ontario Secondary School Dancefest{eventYear ? ` ${eventYear}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  As a participant of Ontario Secondary School Dancefest{eventYear ? ` ${eventYear}` : ''}, I understand that I am an ambassador of my school and OSSDF; therefore, the privilege to submit original dance compositions for entry to the OSSDF comes with behaviour expectations. Furthermore, I understand as a vital participant of this event, I will comply with the following expectations:
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {[
                  { n: 1, text: <><strong>ALL CHOREOGRAPHY MUST BE ORIGINAL and COMPOSED BY THE STUDENT</strong> or, it is a collaboration of student work created in the secondary school environment. ANY choreography taken from dances on YouTube, TikTok or other media platforms and/or choreographed by studio teachers or graduate students from a private dance studio IS NOT PERMITTED. THIS IS PLAGIARISM!</> },
                  { n: 2, text: <>Respond with <strong>positive appreciation</strong> to students from every school committed to the Ontario Secondary School Dancefest.</> },
                  { n: 3, text: <><strong>ALL MUSIC MUST BE APPROPRIATE</strong> in content, cultural meaning and lyrics. Inappropriate choices will be disqualified.</> },
                  { n: 4, text: <>Costumes must be original and should be simple and effective with a focus on the artistic intent of the student choreographer. Student choreographers should seek to collaborate in the costume selection process with student dancers and receive their consent in wearing them. Costumes should also include the use of appropriate undergarments as an integrated element that maintains the artistic integrity of the work. Please come to the live event with lots of pins, tape and sewing kits to remove any and/or all risk of costume malfunctions. Furthermore, <strong>COSTUMES BORROWED FROM PRIVATE DANCE STUDIOS ARE NOT PREMITTED</strong>.</> },
                  { n: 5, text: <>A payment of {participationFee != null ? `$${participationFee.toFixed(2)}` : 'a registration fee'} is required by OSSDF to register as a Participant of OSSDF {eventYear ? ` ${eventYear}` : ''}. Dance entry fees are in addition to this registration fee.</> },
                ].map(({ n, text }) => (
                  <Box key={n} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>{n}.</Typography>
                    <Typography variant="body2" color="text.secondary">{text}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Acknowledgement *</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  As a participant of OSSDF{eventYear ? ` ${eventYear}` : ''}, I have read the information above and understand the importance of following the 5 expectations as explained.
                </Typography>
                {waiverError && <Alert severity="error" sx={{ mb: 2 }}>Please check all boxes to continue.</Alert>}
                {WAIVER_ITEMS.map(item => (
                  <CheckItem key={item.key} checked={waiver[item.key]} label={item.label}
                    onChange={e => { setWaiver(prev => ({ ...prev, [item.key]: e.target.checked })); if (waiverError) setWaiverError(false); }} />
                ))}
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Participant &amp; Entry Fees</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  My Participant Fee and Dance Entry Fee(s) will be sent TO MY TEACHER/SCHOOL by the due date outlined by my dance teacher/coach.
                </Typography>
                <CheckItem checked={feesAgree} label="Yes, I understand and my fees will be sent to my Teacher/School right away and paid by my Teacher."
                  onChange={e => { setFeesAgree(e.target.checked); if (waiverError) setWaiverError(false); }} />
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={handleBack}>Back</Button>
              <Button variant="contained" onClick={handleNext}>Next: Parent Consent</Button>
            </Box>
          </>
        )}

        {/* ── Step 2: Parent Consent ───────────────────────────────────────── */}
        {step === 2 && (
          <>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A parent or guardian must complete a consent and media release form. Choose how you'd like to do this:
            </Typography>

            {/* Choice cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              {[
                { mode: 'now', Icon: GroupsIcon, title: 'Complete together now', desc: 'Parent or guardian is present and can fill in their details here, or student is 18 or above.' },
                { mode: 'separate', Icon: SendIcon, title: 'Send to parent/guardian', desc: 'Get a shareable link to send to your parent or guardian to complete on their own.' },
              ].map(({ mode, Icon, title, desc }) => (
                <Card key={mode} onClick={() => {
                  setParentMode(mode);
                  if (mode === 'separate') setSendDialogOpen(true);
                }} variant="outlined"
                  sx={{
                    cursor: 'pointer', transition: 'all 0.15s',
                    borderColor: parentMode === mode ? 'primary.main' : 'divider',
                    borderWidth: parentMode === mode ? 2 : 1,
                    bgcolor: parentMode === mode ? 'primary.50' : 'background.paper',
                    '&:hover': { boxShadow: 2 },
                  }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Icon color={parentMode === mode ? 'primary' : 'action'} sx={{ fontSize: 36, mb: 1 }} />
                    <Typography fontWeight={600} gutterBottom>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">{desc}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Parent / Guardian Contact</DialogTitle>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  We'll send the consent link to this email.
                </Typography>
                <TextField
                  label="Parent/Guardian Name" required fullWidth size="small"
                  value={sendForm.name}
                  onChange={e => { setSendForm(p => ({ ...p, name: e.target.value })); if (sendErrors.name) setSendErrors(p => ({ ...p, name: '' })); }}
                  error={!!sendErrors.name} helperText={sendErrors.name}
                />
                <TextField
                  label="Parent/Guardian Email" required fullWidth size="small" type="email"
                  value={sendForm.email}
                  onChange={e => { setSendForm(p => ({ ...p, email: e.target.value })); if (sendErrors.email) setSendErrors(p => ({ ...p, email: '' })); }}
                  error={!!sendErrors.email} helperText={sendErrors.email}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setSendDialogOpen(false); setParentMode(''); }}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirmSend}>Confirm</Button>
              </DialogActions>
            </Dialog>

            {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={handleBack}>Back</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={!parentMode || submitting}>
                {submitting
                  ? <CircularProgress size={20} color="inherit" />
                  : parentMode === 'now'
                    ? 'Continue'
                    : 'Submit registration'}
              </Button>
            </Box>
          </>
        )}

      </Box>
    </Box>
  );
}
